import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * DoubleDropGammaSqueeze — Gamma Squeeze Panic
 *
 * How it works:
 * - When Option Sellers are forced to abandon lower strikes on the PE side (Drop PE) 
 *   AND simultaneously open new higher strikes on the CE side (Enter CE), it means a gamma squeeze.
 * - Checks `trendAnalysisData` for recent `droppedPE` and `enteredCE` arrays.
 */
export const evaluateDoubleDropGammaSqueeze: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap, activeRange } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    if (fullTrendData.length < 1) return null;

    const latestRow = fullTrendData[0];
    
    // Fallback parsing if we store these as arrays in trendAnalysisData or rely on raw counts
    // Assuming trendAnalysisData contains `droppedPE`, `enteredCE` arrays, or we can approximate 
    // by checking if totalPutNegative > 0 and totalCallPositive > 0 (which is the mathematical equivalent)
    const totalPutNegative = latestRow?.totalPutNegative ?? 0;
    const totalCallPositive = latestRow?.totalCallPositive ?? 0;
    
    const totalCallNegative = latestRow?.totalCallNegative ?? 0;
    const totalPutPositive = latestRow?.totalPutPositive ?? 0;

    let status: 'BULL' | 'BEAR' | 'Sideways' = 'Sideways';
    let remarks = '';

    // Squeeze Bullish: Put writers dropping strikes (negative) AND Call writers shifting higher (positive)
    if (totalPutNegative >= 2 && totalCallPositive >= 2) {
        status = 'BULL';
        remarks = `Gamma Squeeze: Drop PE (${totalPutNegative}) + Enter CE (${totalCallPositive})`;
    }
    // Squeeze Bearish: Call writers dropping strikes (negative) AND Put writers shifting lower (positive)
    else if (totalCallNegative >= 2 && totalPutPositive >= 2) {
        status = 'BEAR';
        remarks = `Gamma Squeeze: Drop CE (${totalCallNegative}) + Enter PE (${totalPutPositive})`;
    }

    if (status === 'Sideways') return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const optionType = status === 'BULL' ? 'CE' : 'PE';
    
    // Strategy specifically asked for OTM option during gamma squeeze
    const otmStrike = status === 'BULL' ? atmStrike + gap : atmStrike - gap;

    let entryPrice = 0;
    const targetRow = liveFlatOpts.find((r: any) => r.strike === otmStrike && r.type === optionType);
    if (targetRow) {
        entryPrice = targetRow.ltp || targetRow.close || targetRow.price || 0;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status,
        strike: otmStrike, // We return the OTM strike
        option_type: optionType,
        entry_price: entryPrice,
        remarks: remarks
    };
};
